import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { api } from "@/lib/api"
import type { CategoryEntry, PaperCard } from "@/types/api"
import { PaperCardItem } from "@/components/papers/PaperCard"
import { HighlightSnippet } from "@/components/papers/Highlight"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { formatRelative } from "@/lib/utils"

const WINDOWS = ["24h", "7d", "30d", "all"] as const
type Window = (typeof WINDOWS)[number]

type SearchHit = {
  paper: PaperCard
  match_score: number
  highlights: Record<string, string[]>
}

export default function PapersPage() {
  const [q, setQ] = useState("")
  const [submitted, setSubmitted] = useState("")
  const [category, setCategory] = useState<string | undefined>(undefined)
  const [window, setWindow] = useState<Window>("7d")
  const [withCode, setWithCode] = useState(false)

  const isSearch = !!submitted

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set("window", window)
    p.set("limit", "30")
    if (isSearch) {
      p.set("q", submitted)
      p.set("with_highlight", "true")
    } else {
      if (category) p.set("category", category)
      if (withCode) p.set("with_code_only", "true")
      p.set("with_facets", "true")
    }
    return p.toString()
  }, [isSearch, submitted, category, window, withCode])

  const { data: cats } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get<CategoryEntry[]>("/categories"),
    staleTime: 5 * 60_000,
  })

  const listQuery = useQuery<SearchHit[] | { data: PaperCard[] }>({
    queryKey: ["papers-list", { isSearch, params }],
    queryFn: () =>
      isSearch
        ? api.get<SearchHit[]>(`/search?${params}`)
        : api.getEnvelope<PaperCard[]>(`/papers?${params}`),
  })

  const hits: SearchHit[] | undefined = isSearch ? (listQuery.data as SearchHit[]) : undefined
  const cards: PaperCard[] | undefined = !isSearch
    ? (listQuery.data as { data: PaperCard[] })?.data
    : undefined

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">논문 검색</h1>
      </header>

      <form
        className="mb-4 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          setSubmitted(q.trim())
        }}
      >
        <Input
          placeholder="제목 / 초록 / 요약 검색 (한·영)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Button type="submit">검색</Button>
        {isSearch && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => { setQ(""); setSubmitted("") }}
          >
            초기화
          </Button>
        )}
      </form>

      {!isSearch && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">윈도우:</span>
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              className={`rounded-md px-2 py-1 ${
                window === w
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground"
              }`}
            >
              {w}
            </button>
          ))}
          <span className="ml-3 text-muted-foreground">카테고리:</span>
          <button
            onClick={() => setCategory(undefined)}
            className={`rounded-md px-2 py-1 ${
              !category ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
            }`}
          >
            전체
          </button>
          {cats?.slice(0, 10).map((c) => (
            <button
              key={c.tag}
              onClick={() => setCategory(c.tag)}
              className={`rounded-md px-2 py-1 ${
                category === c.tag
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground"
              }`}
              title={c.label_ko}
            >
              {c.tag}
              {c.paper_count_7d != null && c.paper_count_7d > 0 && (
                <span className="ml-1 opacity-70">{c.paper_count_7d}</span>
              )}
            </button>
          ))}
          <label className="ml-3 flex items-center gap-1 text-muted-foreground">
            <input
              type="checkbox"
              checked={withCode}
              onChange={(e) => setWithCode(e.target.checked)}
            />
            코드 있는 것만
          </label>
        </div>
      )}

      {listQuery.isLoading && <div className="text-sm text-muted-foreground">불러오는 중...</div>}
      {listQuery.error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {(listQuery.error as Error).message}
        </div>
      )}

      {hits && (
        <div className="space-y-3">
          {hits.map((h) => {
            const p = h.paper
            const title = p.title_ko || p.title_en
            const titleHL = h.highlights.title_ko?.[0] || h.highlights.title_en?.[0]
            const bodyHL =
              h.highlights.summary_long?.[0] ||
              h.highlights.abstract_en?.[0] ||
              h.highlights.summary_3line?.[0]
            return (
              <article
                key={p.id}
                className="rounded-lg border border-border bg-card p-5 transition-shadow hover:shadow-sm"
              >
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>{formatRelative(p.published_at)}</span>
                  <span>·</span>
                  <span>{p.primary_category}</span>
                  {p.tags.slice(0, 3).map((t) => (
                    <span key={t} className="rounded-md bg-secondary px-2 py-0.5">
                      {t}
                    </span>
                  ))}
                  <span className="ml-auto rounded-md bg-primary/10 px-2 py-0.5 text-primary">
                    match {h.match_score.toFixed(1)}
                  </span>
                </div>
                <h2 className="mb-1 text-base font-semibold leading-snug">
                  <Link to={`/papers/${p.id}`} className="hover:text-primary">
                    {titleHL ? <HighlightSnippet html={titleHL} /> : title}
                  </Link>
                </h2>
                {bodyHL ? (
                  <p className="text-sm text-foreground/80">
                    <HighlightSnippet html={bodyHL} />
                  </p>
                ) : (
                  p.tldr_ko && <p className="text-sm text-foreground/80">{p.tldr_ko}</p>
                )}
                {p.arxiv_id && (
                  <div className="mt-2 text-xs text-muted-foreground">arXiv:{p.arxiv_id}</div>
                )}
              </article>
            )
          })}
          {hits.length === 0 && (
            <div className="rounded-md border border-border p-6 text-center text-sm text-muted-foreground">
              검색 결과 없음
            </div>
          )}
        </div>
      )}

      {cards && (
        <div className="space-y-3">
          {cards.map((p) => <PaperCardItem key={p.id} paper={p} />)}
          {cards.length === 0 && (
            <div className="rounded-md border border-border p-6 text-center text-sm text-muted-foreground">
              결과 없음
            </div>
          )}
        </div>
      )}
    </div>
  )
}
